import React from 'react'
import { Lock, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HubCardProps {
  title: string
  subtitle: string
  icon: React.ReactNode
  color: 'green' | 'blue' | 'purple' | 'orange' | 'cyan'
  completionPercent: number
  isComplete: boolean
  canAccess: boolean
  lockedMessage?: string
  isNextStep?: boolean
  isLocked?: boolean
  onToggleLock?: () => void
  onClick: () => void
}

const COLOR_MAP = {
  green: {
    badge: 'bg-green-500/10 dark:bg-green-500/20',
    icon: 'text-green-600 dark:text-green-400',
    progress: 'bg-green-600 dark:bg-green-400',
    progressBg: 'bg-green-100 dark:bg-green-900/30',
    percent: 'text-green-600 dark:text-green-400',
    activeBorder: 'border-green-600 dark:border-green-400 shadow-lg shadow-green-600/20 dark:shadow-green-400/30',
    hoverBorder: 'border-green-200/50 dark:border-green-800/30 hover:border-green-400 dark:hover:border-green-600 hover:shadow-md',
    lockBtnColor: 'text-green-500 dark:text-green-400',
  },
  blue: {
    badge: 'bg-blue-500/10 dark:bg-blue-500/20',
    icon: 'text-blue-600 dark:text-blue-400',
    progress: 'bg-gradient-to-r from-[#69adba] via-[#8bc6d0] to-[#69adba]',
    progressBg: 'bg-blue-100 dark:bg-blue-900/30',
    percent: 'text-[#69adba] dark:text-[#69adba]',
    activeBorder: 'border-blue-600 dark:border-blue-400 shadow-lg shadow-blue-600/20 dark:shadow-blue-400/30',
    hoverBorder: 'border-blue-200/50 dark:border-blue-800/30 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md',
    lockBtnColor: 'text-blue-500 dark:text-blue-400',
  },
  purple: {
    badge: 'bg-purple-500/10 dark:bg-purple-500/20',
    icon: 'text-purple-600 dark:text-purple-400',
    progress: 'bg-purple-600 dark:bg-purple-400',
    progressBg: 'bg-purple-100 dark:bg-purple-900/30',
    percent: 'text-purple-600 dark:text-purple-400',
    activeBorder: 'border-purple-600 dark:border-purple-400 shadow-lg shadow-purple-600/20 dark:shadow-purple-400/30',
    hoverBorder: 'border-purple-200/50 dark:border-purple-800/30 hover:border-purple-400 dark:hover:border-purple-600 hover:shadow-md',
    lockBtnColor: 'text-purple-500 dark:text-purple-400',
  },
  orange: {
    badge: 'bg-orange-500/10 dark:bg-orange-500/20',
    icon: 'text-orange-600 dark:text-orange-400',
    progress: 'bg-orange-600 dark:bg-orange-400',
    progressBg: 'bg-orange-100 dark:bg-orange-900/30',
    percent: 'text-orange-600 dark:text-orange-400',
    activeBorder: 'border-orange-600 dark:border-orange-400 shadow-lg shadow-orange-600/20 dark:shadow-orange-400/30',
    hoverBorder: 'border-orange-200/50 dark:border-orange-800/30 hover:border-orange-400 dark:hover:border-orange-600 hover:shadow-md',
    lockBtnColor: 'text-orange-500 dark:text-orange-400',
  },
  cyan: {
    badge: 'bg-cyan-500/10 dark:bg-cyan-500/20',
    icon: 'text-cyan-600 dark:text-cyan-400',
    progress: 'bg-cyan-600 dark:bg-cyan-400',
    progressBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    percent: 'text-cyan-600 dark:text-cyan-400',
    activeBorder: 'border-cyan-600 dark:border-cyan-400 shadow-lg shadow-cyan-600/20 dark:shadow-cyan-400/30',
    hoverBorder: 'border-cyan-200/50 dark:border-cyan-800/30 hover:border-cyan-400 dark:hover:border-cyan-600 hover:shadow-md',
    lockBtnColor: 'text-cyan-500 dark:text-cyan-400',
  },
}

export const HubCard: React.FC<HubCardProps> = ({
  title,
  subtitle,
  icon,
  color,
  completionPercent,
  isComplete,
  canAccess,
  lockedMessage,
  isNextStep = false,
  isLocked = false,
  onToggleLock,
  onClick,
}) => {
  const c = COLOR_MAP[color]

  return (
    <div
      onClick={() => canAccess && onClick()}
      className={cn(
        'group relative w-full p-3 rounded-xl border-2 transition-all duration-300',
        isNextStep
          ? cn(c.activeBorder, 'cursor-pointer bg-white dark:bg-slate-800 ring-2 ring-offset-0 animate-pulse-slow')
          : canAccess
          ? cn(c.hoverBorder, 'cursor-pointer bg-white dark:bg-slate-800')
          : 'border-slate-200 dark:border-slate-700 opacity-50 bg-slate-50 dark:bg-slate-800/20'
      )}
    >
      {/* Locked overlay */}
      {!canAccess && lockedMessage && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/5 dark:bg-slate-900/10 backdrop-blur-[0.5px] rounded-xl z-10 pointer-events-none">
          <div className="text-center bg-white/85 dark:bg-slate-800/85 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-600">
            <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300 mx-auto mb-1" />
            <p className="text-xs text-slate-700 dark:text-slate-200 font-semibold">{lockedMessage}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', c.badge)}>
            {icon}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">{title}</h4>
            <p className="text-xs text-secondary-600 dark:text-slate-400">{subtitle}</p>
            <p className={cn('text-xs font-medium mt-0.5', c.percent)}>{completionPercent}% complete</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canAccess && !isNextStep && onToggleLock && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleLock()
              }}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={isLocked ? 'Unlock hub' : 'Lock hub'}
            >
              <Lock className={cn('h-3.5 w-3.5', isLocked ? 'text-slate-500 dark:text-slate-400' : c.lockBtnColor)} />
            </button>
          )}
          {isComplete ? (
            <CheckCircle2 className={cn('h-4 w-4', c.icon)} />
          ) : canAccess ? (
            <div className={cn('h-4 w-4 rounded-full border-2 flex items-center justify-center',
              `border-${color === 'blue' ? '[#69adba]' : color}-300 dark:border-${color}-600`
            )}>
              <div className={cn('h-1.5 w-1.5 rounded-full',
                `bg-${color === 'blue' ? '[#69adba]' : color}-500 dark:bg-${color}-400`
              )} />
            </div>
          ) : null}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className={cn('h-2 rounded-full overflow-hidden', c.progressBg)}>
          <div
            className={cn('h-full transition-all duration-500 rounded-full', c.progress)}
            style={{ width: `${completionPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}
